import React, { useCallback } from 'react';
import { useForm } from '~/hooks';
import { useValidation } from '~/hooks/useValidation';
import { useSectionLoader } from '../../hooks';
import sectionTypes from '../sectionTypes';
import { BsFillExclamationCircleFill } from 'react-icons/bs';

/**
 * @typedef TitleInsuranceReviewValues
 * @property {number}   sectionId
 * @property {string}   titleOpinionId * 
 * @typedef FormState
 * @property {RestrictiveCovenantsReviewValues} values The current form values
 */

/** @type {TitleInsuranceReviewValues} */

const defaultState = {
    sectionId: 0
};

const validationSchema = {
    ownerInsuranceCoverageAmount: {
        validate: (field, data) => {
            if (data?.needOwnerCoverage === 'No') return true;
            return !!field;
        }
    },
    lenderInsuranceCoverageAmount: {
        validate: (field, data) => {
            return data.isCashSale || !!field;
        }
    },
    requiredALTAPrimary: {
        validate: (field, data) => {
            return data.isCashSale || !!field;
        }
    },
    mortgageClausePrimary: {
        validate: (field, data) => {
            return data.isCashSale || !!field;
        }
    },
    loanTypePrimary: {
        validate: (field, data) => {
            return data.isCashSale || !!field;
        }
    },
    loanNumberPrimary: {
        validate: (field, data) => {
            return data.isCashSale || field?.length > 0;
        }
    }
};

const validateSectionDetails = (data, isCashSale) => {
    if (!data.sectionDetails) return { insured: true, lender: true };

    const invalidFields = {};
    const insured = data.sectionDetails.find(x => x._data.type === 'insured');
    const lender = data.sectionDetails.find(x => x._data.type === 'lenderPrimary');

    if (!insured && (!data.needOwnerCoverage || data.needOwnerCoverage === 'Yes') && !data.sameAsOwner) invalidFields.insured = true;
    if (!lender && !isCashSale) invalidFields.lender = true;

    return invalidFields;
};

const TitleInsuranceReview = ({ titleOpinionId, validate, validationResult }) => {
    const { onLoad: loadSection } = useSectionLoader(sectionTypes.TITLE_INSURANCE_APPLICATION, titleOpinionId, sectionTypes.PROPERTY_INFO);

    const onLoad = useCallback((values) => {
        return loadSection(values);
    }, [titleOpinionId]);

    const {
        /** @type {FormState} */
        state,
        //fieldActions
    } = useForm({
        /** @type {ReviewValues} */
        initialState: { ...defaultState },
        onLoad,
        debug: true
    });

    const insuredDetails = state.values && state.values.sectionDetails ? state.values.sectionDetails?.filter(f => f._data.type === 'insured').map((detail) => {
        const primaryInsuredName = detail._data.insuredPrimaryType.value == 'Individual' ?
            (Object.values(detail._data.insuredPrimary).join(' '))
            : detail._data.insuredPrimary.insuredEntityName;

        const secondaryInsuredName = detail._data.insuredSecondary ?
            detail._data.insuredSecondaryType.value == 'Individual' ?
                (Object.values(detail._data.insuredSecondary).join(' '))
                : detail._data.insuredSecondary.insuredEntityName
            :
            null;

        const relationshipLabel = detail._data.relationship?.label || '';

        const insuredName = secondaryInsuredName ? primaryInsuredName + ' ' + relationshipLabel + ' ' + secondaryInsuredName : (primaryInsuredName + ' ' + relationshipLabel);

        return insuredName;
    }) : [];



    const lenderDetail = (state.values.sectionDetails && Object.keys(state.values.sectionDetails).length > 0) ?
        state.values.sectionDetails.filter(f => f._data.type == 'lenderPrimary').map((detail) => {
            return detail._data.lenderDetailData ? detail._data.lenderDetailData.lenderName : '';
        }) : [];

    const lenderSecondaryDetail = (state.values.sectionDetails && Object.keys(state.values.sectionDetails).length > 0) ?
        state.values.sectionDetails.filter(f => f._data.type == 'lenderSecondary').map((detail) => {
            return detail._data.lenderDetailData ? detail._data.lenderDetailData.lenderName : '';
        }) : [];

    const requiredALTAPrimary = state.values.requiredALTAPrimary ? state.values.requiredALTAPrimary.map(x => x.label) : {};

    const requiredALTASecondary = state.values.requiredALTASecondary ? state.values.requiredALTASecondary.map(x => x.label) : {};


    let errorSection = false;
    if (validate) {
        if (!state.values.sectionId) {
            errorSection = true;
        } else {

            if ((state.values.additionalSection && !state.values.additionalSection.cashSale)
                && (
                    !state.values.mortgageClausePrimary
                    || !state.values.requiredALTAPrimary || (state.values.requiredALTAPrimary === '') || state.values.requiredALTAPrimary.length === 0
                    || !state.values.loanTypePrimary || (state.values.loanTypePrimary === '') || state.values.loanTypePrimary.length === 0
                    || !state.values.lenderInsuranceCoverageAmount || (state.values.lenderInsuranceCoverageAmount === '')
                    || !state.values.loanNumberPrimary || (state.values.loanNumberPrimary === '')
                    || !lenderDetail || lenderDetail.length === 0)
            ) {
                errorSection = true;
            }

            if (state.values.needOwnerCoverage && state.values.needOwnerCoverage == 'Yes' &&
                (
                    !state.values.ownerInsuranceCoverageAmount || (state.values.ownerInsuranceCoverageAmount === '')
                    || (!state.values.sameAsOwner && (!insuredDetails || insuredDetails.length === 0))
                )) {
                errorSection = true;
            }
        }
        validationResult(errorSection);
    }

    const [, invalidFields] = useValidation({ ...state.values, isCashSale: state.values?.additionalSection?.cashSale }, validationSchema);
    const invalidSections = validateSectionDetails(state.values, state.values?.additionalSection?.cashSale);

    return (

        <div className="ml-2 mr-5">
            {state.values.sectionId === 0 ?
                <div />
                :
                <>
                    <div className="text-1xl text-blue-60 font-bold mb-3">
                        <div className="display:flex; flex-direction:row;">
                            <div className="z-10 flex flex-row h-full  text-xs font-semibold text-blue-60">
                                <div className="mt-1 mb-2">7. <a href={`/preliminaryopinion/${titleOpinionId}/titleInsurance`}>Title Insurance Application</a> </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-span-8 grid grid-cols-8 grid-flow-row gap-x-5 gap-y-3  text-xs mb-4">
                        <div className="col-span-8 text-red-600">
                            {errorSection ?
                                <p className="flex flex-row gap-x-2 mb-2"><BsFillExclamationCircleFill />
                                    You have not provided all the required information in this section.</p>
                                : ''
                            }
                        </div>
                        <div className={`col-span-2 font-semibold ${invalidFields.ownerInsuranceCoverageAmount && 'text-red-600'}`}> Owners&apos;s Insurance Covereage Amount</div>
                        <div className="col-span-2 flex-end">{state.values.ownerInsuranceCoverageAmount}</div>

                        <div className={`col-span-2 font-semibold ${invalidFields.lenderInsuranceCoverageAmount && 'text-red-600'}`}> Lender&apos;s Insurance Covereage Amount</div>
                        <div className="col-span-2 flex-end">{state.values.lenderInsuranceCoverageAmount}</div>

                        <div className={`col-span-2 font-semibold`}> Issue Homeowners Policy?</div>
                        <div className="col-span-2 flex-end">{state.values.issueHomeownersPolicy ? "Yes" : "No"}</div>

                        <div className={`col-span-2 font-semibold ${invalidSections.insured && 'text-red-600'}`}>Insured(s)</div>
                        <div className="col-span-2 flex-end">{state.values.sameAsOwner ? 'Same as seller/ current owner(s)' : (insuredDetails.length > 0 ? insuredDetails.join(', ') : '')}</div>

                        <div className="col-span-2 font-semibold">Is this a private lender ?</div>
                        <div className="col-span-2 flex-end">{state.values.isPrivateLender}</div>

                        <div className={`col-span-2 font-semibold ${invalidSections.lender && 'text-red-600'}`}>Lender Name</div>
                        <div className="col-span-2 flex-end">{lenderDetail.length > 0 ? lenderDetail.join(', ') : ''}</div>

                        <div className={`col-span-2 font-semibold ${invalidFields.mortgageClausePrimary && 'text-red-600'}`}>Mortgage Clause</div>
                        <div className="col-span-2 flex-end">{state.values.mortgageClausePrimary ? state.values.mortgageClausePrimary.label : ''}</div>

                        <div className={`col-span-2 font-semibold ${invalidFields.loanNumberPrimary && 'text-red-600'}`}>Loan Number</div>
                        <div className="col-span-2 flex-end">{state.values.loanNumberPrimary}</div>

                        <div className={`col-span-2 font-semibold ${invalidFields.loanTypePrimary && 'text-red-600'}`}>Loan Type</div>
                        <div className="col-span-2 flex-end">{state.values.loanTypePrimary ? state.values.loanTypePrimary.label : ''}</div>

                        <div className={`col-span-2 font-semibold ${invalidFields.requiredALTAPrimary && 'text-red-600'}`}>Required ALTA Endorsements</div>
                        <div className="col-span-2 flex-end">{requiredALTAPrimary.length > 0 ? requiredALTAPrimary.join(', ') : requiredALTAPrimary[0]}</div>

                        <div className="col-span-2 font-semibold"> Is Closing Protection Letter Required ? </div>
                        <div className="col-span-2 flex-end">{state.values.isClosingProtectionLetterRequired}</div>

                    </div>
                    {lenderSecondaryDetail && lenderSecondaryDetail.length > 0 && state.values.secondaryLenderFlag ?
                        <div className="col-span-8 grid grid-cols-8 grid-flow-row gap-x-5 gap-y-3  text-xs mb-4">

                            <div className="col-span-2 font-semibold"> Lender&apos;s Insurance Covereage Amount</div>
                            <div className="col-span-2 flex-end">{state.values.lenderInsuranceCoverageAmountSecondary}</div>

                            <div className="col-span-2 font-semibold">Is this a private lender ?</div>
                            <div className="col-span-2 flex-end">{state.values.isPrivateLenderSecondary}</div>

                            <div className="col-span-2 font-semibold">Lender Name</div>
                            <div className="col-span-2 flex-end">{lenderSecondaryDetail.length > 0 ? lenderSecondaryDetail.join(', ') : ''}</div>

                            <div className="col-span-2 font-semibold">Mortgage Clause</div>
                            <div className="col-span-2 flex-end">{state.values.mortgageClauseSecondary ? state.values.mortgageClauseSecondary.label : ''}</div>

                            <div className="col-span-2 font-semibold">Loan Number</div>
                            <div className="col-span-2 flex-end">{state.values.loanNumberSecondary}</div>

                            <div className="col-span-2 font-semibold">Loan Type</div>
                            <div className="col-span-2 flex-end">{state.values.loanTypeSecondary ? state.values.loanTypeSecondary.label : ''}</div>

                            <div className="col-span-2 font-semibold">Required ALTA Endorsements</div>
                            <div className="col-span-2 flex-end">{requiredALTASecondary.length > 0 ? requiredALTASecondary.join(', ') : requiredALTASecondary[0]}</div>

                            <div className="col-span-2 font-semibold"> Is Closing Protection Letter Required ? </div>
                            <div className="col-span-2 flex-end">{state.values.isClosingProtectionLetterRequiredSecondary}</div>

                        </div>
                        :
                        <></>
                    }
                </>
            }
        </div>
    );
};

export default TitleInsuranceReview;